import React, { useState, useEffect } from "react";
import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography } from "@mui/material";
import moment from "moment-timezone";
import { getTablaAmenazasVulnerabilidades } from "../../services/amenazas/amenazasVulnerabilidades/amenazasVulnerabilidadesData";

const TablaAmenaza = ({ selectedIds, selectedDates }) => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasSelections = Array.isArray(selectedIds) && selectedIds.length > 0;

  const formatDate = (dateString, timeZone) => {
    const date = moment.utc(dateString).tz(timeZone);
    return date.format("YYYY-MM-DD");
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!selectedIds || selectedIds.length === 0) {
          setTableData([]);
          return;
        }

        setLoading(true);

        const uniqueCombinations = new Set();
        const requests = [];

        for (let i = 0; i < selectedIds.length; i++) {
          const currentId = selectedIds[i];
          const currentDate = selectedDates[i];

          if (!currentDate) continue;

          const formattedDate = formatDate(currentDate, "America/Guayaquil");
          const key = `${currentId}-${formattedDate}`;

          if (!uniqueCombinations.has(key)) {
            uniqueCombinations.add(key);
            requests.push({ id: currentId, date: formattedDate });
          }
        }

        const allData = new Map();
        for (const { id, date } of requests) {
          try {
            const response = await getTablaAmenazasVulnerabilidades(id, date);

            response.forEach((item) => {
              const dataKey = `${item.ame_nombre}-${item.vul_nombre}-${item.act_nombre}`;
              if (!allData.has(dataKey)) {
                allData.set(dataKey, {
                  amenaza: item.ame_nombre,
                  vulnerabilidad: item.vul_nombre,
                  activo: item.act_nombre,
                  promedio: item.promedio_grupo,
                  costo: item.vac_costo,
                  fecha: formatDate(item.vac_fecha_inicio, "America/Guayaquil"),
                });
              }
            });
          } catch (error) {
            console.error(`Error en la solicitud para ID ${id}, fecha ${date}:`, error);
          }
        }

        const mappedData = Array.from(allData.values());
        setTableData(mappedData);
      } catch (error) {
        console.error("Error general:", error);
      } finally {
        setLoading(false);
      }
    };

    if (hasSelections) {
      fetchData();
    } else {
      setTableData([]);
    }
  }, [selectedIds, selectedDates]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: '#001F3F' }}>
            Tabla de Amenazas
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#001F3F' }}>
                <TableRow>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.85rem' }}>Amenaza</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.85rem' }}>Vulnerabilidad</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.85rem' }}>Activo</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.85rem' }}>Valor Impacto Promedio</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.85rem' }}>Costo</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.85rem' }}>Fecha</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                      <Typography color="textSecondary" variant="body2">Cargando datos...</Typography>
                    </TableCell>
                  </TableRow>
                ) : !hasSelections ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                      <Typography color="textSecondary" variant="body2">Arrastra uno o más activos para visualizar su evaluación.</Typography>
                    </TableCell>
                  </TableRow>
                ) : tableData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                      <Typography color="textSecondary" variant="body2">No hay datos para mostrar en el rango seleccionado.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row, index) => (
                    <TableRow key={`${row.amenaza}-${row.vulnerabilidad}-${row.activo}-${index}`} hover>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{row.amenaza}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{row.vulnerabilidad}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{row.activo}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem' }}>{Number(row.promedio).toFixed(2)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem' }}>${row.costo}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem' }}>{row.fecha}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TablaAmenaza;
